import type { TypedDocumentNode } from '@apollo/client';
import type { ResultOf } from '@newfront-insurance/graphql-frontend';
import { graphql } from '@newfront-insurance/graphql-frontend';

export type ChatAppType = 'OUTLOOK' | 'PPT' | 'NAVIGATOR';
export type ChatLifecycleStatus = 'ACTIVE' | 'ARCHIVED';

export const ACTIVE_CHAT_STATUS: ChatLifecycleStatus = 'ACTIVE';
export const ARCHIVED_CHAT_STATUS: ChatLifecycleStatus = 'ARCHIVED';

export type GetChatsByFiltersChat = {
  uuid?: string | null;
  userUuid: string;
  accountUuid?: string | null;
  account?: {
    uuid?: string | null;
    name: string;
  } | null;
  appType: ChatAppType;
  status: ChatLifecycleStatus;
  title: string;
  summary: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  lastMessageAt?: string | null;
  hasUnreadMessages: boolean;
  activeStreamId?: string | null;
  pinnedAt?: string | null;
};

export type GetChatsByFilters = GetChatsByFiltersChat[];

type GetChatsByFiltersData = {
  chatsList: GetChatsByFilters;
};

type GetChatsByFiltersVariables = {
  userUuid?: string | null;
  accountUuid?: string | null;
  appType?: ChatAppType | null;
  statusFilter?: ChatLifecycleStatus | null;
};

type GetDeletedChatsByFiltersVariables = Omit<GetChatsByFiltersVariables, 'statusFilter'>;

export const GET_CHATS_BY_FILTERS = graphql(`
  query GetChatsByFilters($userUuid: String, $accountUuid: String, $appType: AppType, $statusFilter: ChatStatus) {
    chatsList: getChats(
      userUuid: $userUuid
      accountUuid: $accountUuid
      appType: $appType
      statusFilter: $statusFilter
    ) {
      uuid
      userUuid
      accountUuid
      account {
        uuid
        name
      }
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
      pinnedAt
    }
  }
`) as TypedDocumentNode<GetChatsByFiltersData, GetChatsByFiltersVariables>;

export const GET_DELETED_CHATS_BY_FILTERS = graphql(`
  query GetDeletedChatsByFilters($userUuid: String, $accountUuid: String, $appType: AppType) {
    chatsList: getChats(userUuid: $userUuid, accountUuid: $accountUuid, appType: $appType, deletedOnly: true) {
      uuid
      userUuid
      accountUuid
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
      pinnedAt
    }
  }
`) as TypedDocumentNode<GetChatsByFiltersData, GetDeletedChatsByFiltersVariables>;

export const GET_CHAT_WITH_MESSAGES = graphql(`
  query GetChatWithMessages($uuid: String!) {
    getChat(uuid: $uuid) {
      uuid
      userUuid
      accountUuid
      activeStreamId
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      hasUnreadMessages
      pinnedAt
      appType
      account {
        uuid
        name
      }
      messages {
        uuid
        chatUuid
        reaction
        role
        files {
          uuid
          filename
        }
        parts
        metadata
        createdAt
      }
    }
  }
`);

type GetChatWithMessagesData = ResultOf<typeof GET_CHAT_WITH_MESSAGES>;
type GetChatWithMessagesVariables = { uuid: string };

export type GetChatWithMessages = NonNullable<GetChatWithMessagesData['getChat']>;
export type GetChatWithMessagesMessage = GetChatWithMessages['messages'][number];

type CanSoftDeleteChatThreadsData = {
  canSoftDeleteChatThreads: boolean;
};

export const CAN_SOFT_DELETE_CHAT_THREADS = graphql(`
  query CanSoftDeleteChatThreads {
    canSoftDeleteChatThreads
  }
`) as TypedDocumentNode<CanSoftDeleteChatThreadsData, Record<string, never>>;

export const GET_DELETED_CHAT_WITH_MESSAGES = graphql(`
  query GetDeletedChatWithMessages($uuid: String!) {
    getChat(uuid: $uuid, includeDeleted: true) {
      uuid
      userUuid
      accountUuid
      activeStreamId
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      hasUnreadMessages
      pinnedAt
      appType
      account {
        uuid
        name
      }
      messages {
        uuid
        chatUuid
        reaction
        role
        files {
          uuid
          filename
        }
        parts
        metadata
        createdAt
      }
    }
  }
`) as TypedDocumentNode<GetChatWithMessagesData, GetChatWithMessagesVariables>;

export const GET_ACCOUNT = graphql(`
  query GetAccount($uuid: String!) {
    getAccount(uuid: $uuid) {
      uuid
      name
      programType
    }
  }
`);

export type GetAccount = NonNullable<ResultOf<typeof GET_ACCOUNT>['getAccount']>;

export const GET_SKILLS_FOR_USER = graphql(`
  query GetSkillsForUser($accountUuid: String, $userUuid: String!) {
    skills: getSkillsForUser(accountUuid: $accountUuid, userUuid: $userUuid) {
      id
      source
      sourceDetails {
        type
        id
        ownerEmail
      }
      command
      name
      description
      summary
      overview
      category
      enabled
      exampleUsage
      instructionPlaceholder
      accountRequired
    }
  }
`);

export type GetSkillsForUser = NonNullable<ResultOf<typeof GET_SKILLS_FOR_USER>['skills']>;
export type NovaSkill = GetSkillsForUser[number];

export const GET_CUSTOM_SKILLS = graphql(`
  query GetCustomSkills($filters: CustomSkillFiltersInput) {
    customSkills: getCustomSkills(filters: $filters) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export type GetCustomSkills = NonNullable<ResultOf<typeof GET_CUSTOM_SKILLS>['customSkills']>;

export const GET_SHARED_CUSTOM_SKILLS = graphql(`
  query GetSharedCustomSkills($filters: SharedCustomSkillFiltersInput!) {
    sharedCustomSkills: getSharedCustomSkills(filters: $filters) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export type GetSharedCustomSkills = NonNullable<ResultOf<typeof GET_SHARED_CUSTOM_SKILLS>['sharedCustomSkills']>;
export type CustomSkillRecord = GetCustomSkills[number] | GetSharedCustomSkills[number];

export const GET_CUSTOM_SKILL_COLLECTIONS = graphql(`
  query GetCustomSkillCollections(
    $accountUuid: String
    $userUuid: String!
    $customSkillFilters: CustomSkillFiltersInput
    $sharedCustomSkillFilters: SharedCustomSkillFiltersInput!
    $includeCustomSkills: Boolean!
    $includeSharedCustomSkills: Boolean!
  ) {
    runtimeSkills: getSkillsForUser(accountUuid: $accountUuid, userUuid: $userUuid) {
      id
      source
      sourceDetails {
        type
        id
        ownerEmail
      }
      command
      name
      description
      summary
      overview
      category
      enabled
      exampleUsage
      instructionPlaceholder
      accountRequired
    }
    customSkills: getCustomSkills(filters: $customSkillFilters) @include(if: $includeCustomSkills) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
    sharedCustomSkills: getSharedCustomSkills(filters: $sharedCustomSkillFilters)
      @include(if: $includeSharedCustomSkills) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const GET_CUSTOM_SKILL_SHARES = graphql(`
  query GetCustomSkillShares($filters: CustomSkillShareFiltersInput!) {
    customSkillShares: getCustomSkillShares(filters: $filters) {
      customSkillUuid
      userUuid
      permission
      createdAt
      updatedAt
    }
  }
`);

export type CustomSkillShareRecord = NonNullable<ResultOf<typeof GET_CUSTOM_SKILL_SHARES>['customSkillShares']>[number];

export const GET_CUSTOM_SKILL_SHARE_RECIPIENTS = graphql(`
  query GetCustomSkillShareRecipients($filters: CustomSkillShareFiltersInput!) {
    customSkillShares: getCustomSkillShares(filters: $filters) {
      customSkillUuid
      userUuid
      permission
      createdAt
      updatedAt
      user {
        uuid
        email
        firstName
        lastName
      }
    }
  }
`);

export const SEARCH_NOVA_SHAREABLE_USERS = graphql(`
  query SearchNovaShareableUsers($query: String!, $limit: Int) {
    employees: searchNovaShareableUsers(query: $query, limit: $limit) {
      userUuid
      email
      firstName
      lastName
      fullName
      imageUrl
      title
    }
  }
`);
